import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowUpDown, ChevronLeft, ChevronRight, Search, Inbox } from "lucide-react";

export function DataTable({ columns, data, searchable = true, pageSize = 10, onRowClick, testId = "data-table", emptyText = "Belum ada data" }) {
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let rows = data || [];
    if (query) {
      const q = query.toLowerCase();
      rows = rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
    }
    if (sortKey) {
      rows = [...rows].sort((a, b) => {
        const av = a[sortKey], bv = b[sortKey];
        if (av === bv) return 0;
        const r = av > bv ? 1 : -1;
        return sortDir === "asc" ? r : -r;
      });
    }
    return rows;
  }, [data, query, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageData = filtered.slice((page - 1) * pageSize, page * pageSize);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  return (
    <div data-testid={testId} className="space-y-3">
      {searchable && (
        <div className="relative max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid={`${testId}-search`} placeholder="Cari..." value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }} className="pl-9" />
        </div>
      )}
      <div className="rounded-md border bg-card overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40">
              {columns.map((c) => (
                <th key={c.key} className="text-left font-semibold text-xs uppercase tracking-wide text-muted-foreground px-4 py-3 whitespace-nowrap">
                  {c.sortable !== false ? (
                    <button className="inline-flex items-center gap-1 hover:text-foreground transition-colors" onClick={() => toggleSort(c.key)}>
                      {c.label} <ArrowUpDown className="h-3 w-3" />
                    </button>
                  ) : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-16 text-center text-muted-foreground">
                  <Inbox className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  {emptyText}
                </td>
              </tr>
            ) : pageData.map((row, i) => (
              <tr key={row.id || i}
                data-testid={`${testId}-row-${i}`}
                className={`border-b last:border-0 transition-colors hover:bg-white/5 ${onRowClick ? "cursor-pointer" : ""}`}
                onClick={() => onRowClick && onRowClick(row)}>
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-3 ${c.numeric ? "text-right tabular-nums" : ""}`}>
                    {c.render ? c.render(row) : (row[c.key] ?? "-")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{filtered.length} data</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid={`${testId}-prev`}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span>Hal {page}/{totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid={`${testId}-next`}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
